import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Images, Plus, Star } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Field } from "@/components/common";
import { Confirm, FormDialog } from "@/components/kids/kidsUi";
import { fmtDate, todayIn } from "@/lib/format";
import { useBlobUrl, uploadMany } from "@/components/inventory/util";

function Photo({ url, alt, testid }) {
  const src = useBlobUrl(url);
  return <span className="block aspect-square w-24 overflow-hidden rounded-xl bg-q-raised" data-testid={testid}>{src && <img src={src} alt={alt} className="h-full w-full object-cover" />}</span>;
}

function AddDialog({ hubId, base, presets, tz, onClose, onSaved }) {
  const [f, setF] = useState({ title: presets[0], custom: "", date: todayIn(tz), note: "" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (files.length > 3) { toast.error("Up to 3 photos per milestone"); return; }
    setBusy(true);
    try {
      const photo_upload_ids = await uploadMany(`/organizations/${hubId}/health`, files);
      const { data } = await api.post(`${base}/milestones`, { title: f.title === "custom" ? f.custom : f.title, date: f.date, note: f.note, photo_upload_ids });
      onSaved(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const cls = "q-input min-h-[44px] w-full";
  return (
    <FormDialog open onClose={onClose} title="Add a milestone" testid="milestone-dialog">
      <div className="space-y-3">
        <Field label="Milestone"><select className={cls} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="milestone-title">{presets.map((p) => <option key={p}>{p}</option>)}<option value="custom">Custom…</option></select></Field>
        {f.title === "custom" && <Field label="Title"><input className={cls} value={f.custom} onChange={(e) => setF({ ...f, custom: e.target.value })} data-testid="milestone-custom" /></Field>}
        <Field label="Date"><input type="date" className={cls} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="milestone-date" /></Field>
        <Field label="Note"><input className={cls} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="milestone-note" /></Field>
        <Field label="Photos (up to 3)"><input type="file" multiple accept="image/jpeg,image/png,image/heic,.jpg,.jpeg,.png,.heic" className={cls} onChange={(e) => setFiles([...e.target.files].slice(0, 3))} data-testid="milestone-photos" /></Field>
        <button className="q-btn q-btn-primary min-h-[44px] w-full" disabled={busy} onClick={save} data-testid="milestone-save">{busy ? "Saving…" : "Save"}</button>
      </div>
    </FormDialog>
  );
}

export default function Milestones({ hubId, base, items, presets, tz, canEdit, locked, reload }) {
  const reduce = useReducedMotion();
  const [add, setAdd] = useState(false);
  const [offer, setOffer] = useState(null);
  const toMemory = (m) => api.post(`${base}/milestones/${m.id}/memory`).then(() => { toast.success("Added to Memories"); setOffer(null); reload(); }).catch((e) => toast.error(errMsg(e)));
  const sorted = [...items].sort((a, b) => (a.date < b.date ? 1 : -1));
  return (
    <section data-testid="health-milestones">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display text-lg font-extrabold text-q-text">Milestones</h3>
        {canEdit && <button className="q-btn q-btn-secondary min-h-[44px]" disabled={locked} onClick={() => setAdd(true)} data-testid="health-add-milestone"><Plus className="h-4 w-4" /> Add milestone</button>}
      </div>
      <ol className="relative ml-3 border-l-2 border-q-line pl-6" data-testid="milestone-timeline">{sorted.map((m, i) => (
        <motion.li key={m.id} className="relative mb-5" initial={reduce ? false : { opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: reduce ? 0 : i * 0.12 }} data-testid={`milestone-${m.id}`}>
          <span className="absolute -left-[37px] top-0 flex h-6 w-6 items-center justify-center rounded-full bg-q-raised"><Star className="h-3.5 w-3.5" style={{ color: "rgb(var(--glow-kids))" }} /></span>
          <p className="font-semibold text-q-text">{m.title} <span className="text-xs font-normal text-q-muted">{fmtDate(m.date)}</span></p>
          {m.note && <p className="text-sm text-q-muted">{m.note}</p>}
          {m.photos?.length > 0 && <div className="mt-2 flex flex-wrap gap-2">{m.photos.map((p) => <Photo key={p} url={`${base}/milestones/${m.id}/photos/${p}`} alt={m.title} testid={`milestone-photo-${p}`} />)}</div>}
          {canEdit && m.photos?.length > 0 && !m.memory_id && <button className="q-btn q-btn-ghost mt-1 min-h-[44px]" disabled={locked} onClick={() => toMemory(m)} data-testid={`milestone-memory-${m.id}`}><Images className="h-4 w-4" /> Also add to Memories</button>}
          {m.memory_id && <p className="text-xs text-q-muted" data-testid={`milestone-in-memories-${m.id}`}>In Memories</p>}
        </motion.li>))}
      </ol>
      {add && <AddDialog hubId={hubId} base={base} presets={presets} tz={tz} onClose={() => setAdd(false)} onSaved={(m) => { setAdd(false); reload(); if (m.photos?.length) setOffer(m); else toast.success("Milestone saved"); }} />}
      <Confirm open={!!offer} onClose={() => setOffer(null)} title="Milestone saved" body="Also add it to Memories?" confirmLabel="Add to Memories" onConfirm={() => toMemory(offer)} testid="milestone-memory-offer" />
    </section>
  );
}
